import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TestCaseVersion } from '../version-history';
import { TestCaseInstanceFields } from './TestCaseInstanceFields';

interface InstanceReviewHistorySectionProps {
  versions: TestCaseVersion[];
}

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'grid',
    gridTemplateColumns: '300px minmax(0, 1fr)',
    alignItems: 'start',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  list: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  item: {
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderBottom: 0,
    },
  },
  preview: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  previewTitle: {
    fontWeight: 600,
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  sourceChip: {
    backgroundColor: 'transparent',
  },
}));

function formatDate(value: string): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatStatus(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export const InstanceReviewHistorySection = ({
  versions,
}: InstanceReviewHistorySectionProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const sortedVersions = useMemo(
    () =>
      [...versions].sort((left, right) => {
        if (right.versionNumber !== left.versionNumber) {
          return right.versionNumber - left.versionNumber;
        }

        return Date.parse(right.createdAt) - Date.parse(left.createdAt);
      }),
    [versions],
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setSelectedVersionId(
      previous =>
        (previous &&
          sortedVersions.some(version => version.versionId === previous) &&
          previous) ||
        sortedVersions[0]?.versionId ||
        null,
    );
  }, [sortedVersions]);

  const selectedVersion =
    sortedVersions.find(version => version.versionId === selectedVersionId) ??
    sortedVersions[0] ??
    null;

  if (sortedVersions.length === 0) {
    return (
      <Alert severity="info">
        {t('testCaseGeneration.instanceReview.history.empty')}
      </Alert>
    );
  }

  return (
    <Box className={classes.layout}>
      <Box className={classes.list}>
        <List disablePadding>
          {sortedVersions.map(version => (
            <ListItem
              key={version.versionId}
              button
              selected={version.versionId === selectedVersion?.versionId}
              className={classes.item}
              onClick={() => setSelectedVersionId(version.versionId)}
            >
              <ListItemText
                primary={t(
                  'testCaseGeneration.instanceReview.history.version',
                  {
                    number: version.versionNumber,
                  },
                )}
                secondary={
                  <>
                    <Typography variant="caption" display="block">
                      {formatStatus(version.lifecycleStatus)}
                    </Typography>

                    <Typography variant="caption" display="block">
                      {version.source} · {formatDate(version.createdAt)}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box className={classes.preview}>
        {selectedVersion && (
          <>
            <Box className={classes.header}>
              <Box>
                <Typography
                  variant="subtitle1"
                  className={classes.previewTitle}
                >
                  {t('testCaseGeneration.instanceReview.history.version', {
                    number: selectedVersion.versionNumber,
                  })}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {formatDate(selectedVersion.createdAt)}
                </Typography>
              </Box>

              <Box className={classes.badges}>
                <Chip
                  size="small"
                  color={
                    selectedVersion.lifecycleStatus === 'CURRENT'
                      ? 'primary'
                      : 'default'
                  }
                  label={formatStatus(selectedVersion.lifecycleStatus)}
                />

                <Chip
                  size="small"
                  variant="outlined"
                  className={classes.sourceChip}
                  label={selectedVersion.source}
                />
              </Box>
            </Box>

            <TestCaseInstanceFields fields={selectedVersion.fields} />
          </>
        )}
      </Box>
    </Box>
  );
};

export default InstanceReviewHistorySection;
